import ShareIcon from '@mui/icons-material/ShareOutlined';
import { Button } from '@teuteuf-games/button';
import { useCallback } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { trackEvent } from '../../utils/analytics';
import { canUseNativeShare, copyTextToClipboard } from '../../utils/share';
import { ItemModalShell } from './ItemModalShell';
import { MatchItem, MatchModeConfig } from './matchConfig';
import { buildItemShareUrl } from './shareLink';

/**
 * Tap a flag and this shows it big — a tile on a phone is too small to judge.
 * The only action is passing the puzzle on; the flag's own city is deliberately
 * never named here, since that is the answer.
 */
export const ItemPreviewModal = ({
  config,
  flag,
  cities,
  onClose,
}: {
  config: MatchModeConfig;
  flag: number | null;
  cities: MatchItem[];
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const i18nBase = config.i18nBase;

  const isOpen = flag !== null;
  const card = isOpen ? cities[flag] : null;

  const share = useCallback(async () => {
    if (!card) return;

    const question = t(`${i18nBase}.share-question`);
    const url = buildItemShareUrl(config, card.id);
    const useNative = isMobile && canUseNativeShare();
    const eventParams = {
      content_type: `flagle_${config.key}_item`,
      item_id: `${config.key}-${card.id}`,
      mode: config.key,
    };

    trackEvent('share_click', {
      ...eventParams,
      method: useNative ? 'native' : 'clipboard',
    });

    if (useNative) {
      try {
        // text and url separately: share targets that render a link preview
        // want the URL on its own.
        await navigator.share({ title: 'Flagle', text: question, url });
        trackEvent('share', { ...eventParams, method: 'native' });
        return;
      } catch (error) {
        // Dismissing the sheet rejects with AbortError — a cancellation, not a
        // failure, and worth separating in reporting.
        if ((error as Error)?.name === 'AbortError') {
          trackEvent('share_cancelled', { ...eventParams, method: 'native' });
          return;
        }
      }
    }

    const copied = await copyTextToClipboard(`${question}\n${url}`);
    if (copied) {
      trackEvent('share', { ...eventParams, method: 'clipboard' });
      toast.success(t('copy'), { autoClose: 2000 });
      return;
    }
    trackEvent('share_failed', {
      ...eventParams,
      method: 'clipboard',
      error: 'clipboard_unavailable',
    });
    toast.error(t('sharing.copyFailed'), { autoClose: 3000 });
  }, [card, config, i18nBase, t]);

  return (
    <ItemModalShell
      config={config}
      open={isOpen}
      onClose={onClose}
      flagUrl={card?.flag ?? ''}
    >
      <Button
        buttonStyle="primary"
        label={t(`${i18nBase}.ask-friend`)}
        width="w-full"
        leadingIcon={<ShareIcon />}
        onClick={share}
      />
    </ItemModalShell>
  );
};
